import {format, endOfWeek, startOfWeek, isSameMonth, isSameYear} from 'date-fns';
import {
  Button,
  DropdownDivider,
  DropdownGroup,
  DropdownItem,
  IconButton,
  openDropdown,
  Segment,
  useTranslationContext,
} from 'platform/components';
import {
  Box,
  getValueByDevice,
  Hide,
  HStack,
  Right,
  Show,
  Stack,
  Text,
  useDevice,
} from 'platform/foundation';
import {convertStringLocaleToDateFns, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {parseDate, pushGtmEvent, RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {DAYS_IN_WEEK} from '../constants/daysInWeek';
import type {BigCalendarUser} from '../types/BigCalendarUser';
import type {BigCalendarUsersProps} from '../types/BigCalendarUsersProps';
import type {BigCalendarViewType} from '../types/BigCalendarViewType';
import {HeaderUserControll} from './HeaderUserControll';
import {HeaderUserControllDialog} from './HeaderUserControllDialog';

interface HeaderProps extends BigCalendarUsersProps, RequiredTestIdProps {
  currentDate: Date;
  view: BigCalendarViewType;
  allowedViews: BigCalendarViewType[];
  onViewChange: (view: BigCalendarViewType) => void;
  onNavigate: (date: Date) => void;
}

export function Header(props: HeaderProps) {
  const t = useTranslationContext();
  const device = useDevice();
  const {language} = useLocale();
  const [isUserControllDialogOpen, openUserControllDialog, closeUserControllDialog] =
    useBoolean(false);

  const handlePrevious = () => {
    const newDate = parseDate(props.currentDate);
    match(props.view)
      .with('month', () => newDate.setMonth(props.currentDate.getMonth() - MONTH_OFFSET)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .with('week', () => newDate.setDate(props.currentDate.getDate() - DAYS_IN_WEEK)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .with('day', () => newDate.setDate(props.currentDate.getDate() - DAY_OFFSET)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .exhaustive();

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_previous_click',
      value: {
        view: props.view,
        newDate: newDate.toISOString(),
      },
    });

    props.onNavigate(newDate);
  };

  const handleNext = () => {
    const newDate = parseDate(props.currentDate);
    match(props.view)
      .with('month', () => newDate.setMonth(props.currentDate.getMonth() + MONTH_OFFSET)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .with('week', () => newDate.setDate(props.currentDate.getDate() + DAYS_IN_WEEK)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .with('day', () => newDate.setDate(props.currentDate.getDate() + DAY_OFFSET)) // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
      .exhaustive();

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_next_click',
      value: {
        view: props.view,
        newDate: newDate.toISOString(),
      },
    });

    props.onNavigate(newDate);
  };

  const onViewChange = (view: BigCalendarViewType) => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_view_change',
      new_value: view,
      previous_value: props.view,
    });
    props.onViewChange(view);
  };

  const onTodayClick = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_navigate_today_click',
      value: {
        view: props.view,
      },
    });
    props.onNavigate(new Date());
  };

  const onUserControlChange = (selectedUsers: BigCalendarUser[]) => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_user_control_change',
      new_value: {
        selectedUsers: selectedUsers.map((user) => user.email),
        numberOfSelectedUsers: selectedUsers.length,
      },
      previous_value: {
        selectedUsers: props.selectedUsers?.map((user) => user.email),
        numberOfSelectedUsers: props.selectedUsers?.length,
      },
    });

    props.onUserControlChange?.(selectedUsers);
  };

  const getWeekTitle = () => {
    const start = startOfWeek(props.currentDate, {weekStartsOn: 1});
    const end = endOfWeek(props.currentDate, {weekStartsOn: 1});
    if (isSameMonth(start, end)) {
      return `${format(start, 'LLLL yyyy', {
        locale: convertStringLocaleToDateFns(language),
      })}`;
    }
    if (isSameYear(start, end)) {
      return `${format(start, 'LLLL', {
        locale: convertStringLocaleToDateFns(language),
      })} - ${format(end, 'LLLL', {
        locale: convertStringLocaleToDateFns(language),
      })}`;
    }
    return `${format(start, 'LLLL yyyy', {
      locale: convertStringLocaleToDateFns(language),
    })} - ${format(end, 'LLLL yyyy', {
      locale: convertStringLocaleToDateFns(language),
    })}`;
  };

  const title = match([props.view, device === 'mobile'])
    .with(
      ['month', false],
      always(
        format(props.currentDate, 'LLLL yyyy', {locale: convertStringLocaleToDateFns(language)})
      )
    )
    .with(['week', false], getWeekTitle)
    .with(
      ['day', false],
      always(
        format(props.currentDate, 'd. LLLL yyyy, EEEE', {
          locale: convertStringLocaleToDateFns(language),
        })
      )
    )
    .with(
      ['month', true],
      always(format(props.currentDate, 'LLLL', {locale: convertStringLocaleToDateFns(language)}))
    )
    .with(
      ['week', true],
      always(format(props.currentDate, 'LLLL', {locale: convertStringLocaleToDateFns(language)}))
    )
    .with(
      ['day', true],
      always(
        format(props.currentDate, 'd. LLL yyyy', {
          locale: convertStringLocaleToDateFns(language),
        })
      )
    )
    .otherwise(always(null));

  return (
    <Stack
      data-testid={suffixTestId('header', props)}
      direction={['column', 'row']}
      align={['flex-start', 'center']}
      justify={['center', 'space-between']}
    >
      <HStack width="100%" spacing={2} align="center">
        <Hide onMobile>
          <Button
            data-testid={suffixTestId('header-todayButton', props)}
            variant="secondary"
            title={t('bigCalendar.today')}
            onClick={onTodayClick}
          />
        </Hide>
        <Show onMobile>
          <IconButton
            data-testid={suffixTestId('header-todayButton', props)}
            icon="action/calendar_today"
            priority="secondary"
            onClick={onTodayClick}
          />
        </Show>
        <IconButton
          data-testid={suffixTestId('header-chevronLeftButton', props)}
          icon="navigation/chevron_left"
          priority="secondary"
          onClick={handlePrevious}
        />
        <IconButton
          data-testid={suffixTestId('header-chevronRightButton', props)}
          icon="navigation/chevron_right"
          priority="secondary"
          onClick={handleNext}
        />
        <Box width={['100%', 'auto']}>
          <Text
            data-testid={suffixTestId('header-title', props)}
            size={getValueByDevice(device, 'base', 'large')}
          >
            {title}
          </Text>
        </Box>
        <Hide onMobile>
          <Show
            when={
              props.users &&
              props.users.length > 0 &&
              typeof props.onUserControlChange === 'function'
                ? props.users
                : null
            }
          >
            {(users) => (
              <HeaderUserControll
                data-testid={suffixTestId('header', props)}
                currentUser={props.currentUser}
                selectedUsers={props.selectedUsers || []}
                users={users}
                onUserControlChange={onUserControlChange}
                onDialogOpen={openUserControllDialog}
              />
            )}
          </Show>
        </Hide>
        <Show onMobile>
          <Right width="100%">
            <IconButton
              data-testid={suffixTestId('header-userControll-iconButton', props)}
              icon="action/settings"
              priority="secondary"
              onClick={(event) =>
                openDropdown(
                  event,
                  <>
                    <Show when={props.allowedViews.length > 1}>
                      <DropdownGroup label={t('general.actions.view')}>
                        <Show when={props.allowedViews.includes('month')}>
                          <DropdownItem
                            data-testid={suffixTestId('header-view-month', props)}
                            isActive={props.view === 'month'}
                            leftIcon="automotive/calendar"
                            label={t('bigCalendar.month')}
                            onClick={() => onViewChange('month')}
                          />
                        </Show>
                        <Show when={props.allowedViews.includes('week')}>
                          <DropdownItem
                            data-testid={suffixTestId('header-view-week', props)}
                            isActive={props.view === 'week'}
                            leftIcon="automotive/calendar"
                            label={t('bigCalendar.week')}
                            onClick={() => onViewChange('week')}
                          />
                        </Show>
                        <Show when={props.allowedViews.includes('day')}>
                          <DropdownItem
                            data-testid={suffixTestId('header-view-day', props)}
                            isActive={props.view === 'day'}
                            leftIcon="automotive/calendar"
                            label={t('bigCalendar.day')}
                            onClick={() => onViewChange('day')}
                          />
                        </Show>
                      </DropdownGroup>
                    </Show>
                    <Show
                      when={
                        props.users &&
                        props.users.length > 0 &&
                        typeof props.onUserControlChange === 'function'
                          ? props.users
                          : null
                      }
                    >
                      {(users) => (
                        <>
                          <Show when={props.allowedViews.length > 1}>
                            <DropdownDivider />
                          </Show>
                          <HeaderUserControll
                            data-testid={suffixTestId('header', props)}
                            currentUser={props.currentUser}
                            selectedUsers={props.selectedUsers || []}
                            users={users}
                            onDialogOpen={openUserControllDialog}
                            onUserControlChange={onUserControlChange}
                          />
                        </>
                      )}
                    </Show>
                  </>
                )
              }
            />
          </Right>
        </Show>
      </HStack>
      <Hide onMobile>
        <Show when={props.allowedViews.length > 1}>
          <Segment
            data-testid={suffixTestId('header-segment', props)}
            value={props.view}
            onChange={onViewChange}
            options={props.allowedViews.map((view) => ({
              value: view,
              label: match(view)
                .with('month', () => t('bigCalendar.month'))
                .with('week', () => t('bigCalendar.week'))
                .with('day', () => t('bigCalendar.day'))
                .exhaustive(),
            }))}
          />
        </Show>
      </Hide>
      <HeaderUserControllDialog
        data-testid={suffixTestId('header', props)}
        isOpen={isUserControllDialogOpen}
        currentUser={props.currentUser}
        users={props.users || []}
        selectedUsers={props.selectedUsers || []}
        onClose={closeUserControllDialog}
        onUserControlChange={onUserControlChange}
      />
    </Stack>
  );
}

const MONTH_OFFSET = 1;
const DAY_OFFSET = 1;
